import { useRef } from "react";
import { useMutation, type DefaultError, type QueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { capturePrivateSession, assertPrivateSessionLifetime, isPrivateSessionCurrent } from "@/lib/api/private-session";

/** Capture at mount, before an old retained MutationObserver can create a fresh request. */
export function usePrivateMutation<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  client?: QueryClient,
) {
  const lifetime = useRef(capturePrivateSession()).current;
  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    onMutate: options.onMutate ? async (...args) => {
      assertPrivateSessionLifetime(lifetime);
      const context = await options.onMutate!(...args);
      assertPrivateSessionLifetime(lifetime);
      return context;
    } : undefined,
    mutationFn: options.mutationFn ? async (...args) => {
      assertPrivateSessionLifetime(lifetime);
      try {
        const result = await options.mutationFn!(...args);
        assertPrivateSessionLifetime(lifetime);
        return result;
      } catch (failure) { assertPrivateSessionLifetime(lifetime); throw failure; }
    } : undefined,
    onSuccess: async (...args) => {
      if (!isPrivateSessionCurrent(lifetime)) return;
      await options.onSuccess?.(...args);
      assertPrivateSessionLifetime(lifetime);
    },
    onError: (...args) => { if (isPrivateSessionCurrent(lifetime)) return options.onError?.(...args); },
    onSettled: async (...args) => {
      if (!isPrivateSessionCurrent(lifetime)) return;
      await options.onSettled?.(...args);
      assertPrivateSessionLifetime(lifetime);
    },
  }, client);
}
